/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootResource } from '@common/api-root-shared';
import { LoadBinaryFileListSuccessProps } from '@common/binary-file-shared';
import { CommandStateResourceProps, LoadCommandListSuccessProps, UpdateCommandProps } from '@alfa-client/command-shared';
import { ApiErrorAction, ResourceUriProps, TypedActionCreator, TypedActionCreatorWithProps } from '@common/tech-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { createAction, props } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { VorgangWithEingangLinkRel } from '../vorgang.linkrel';
import { AdditionalActions, VorgangListResource, VorgangWithEingangResource } from '../vorgang.model';

export interface SearchVorgaengeByProps {
  apiRoot: ApiRootResource;
  searchString: string;
  linkRel: string;
}

export interface StringBasedProps {
  string: string;
}

export interface ApiRootAction {
  apiRoot: ApiRootResource;
}

export interface ApiRootWithLinkRelProps {
  apiRoot: ApiRootResource;
  linkRel: string;
}

export interface HttpErrorAction {
  httpErrorResponse: HttpErrorResponse;
}

export interface VorgangListAction {
  vorgangList: VorgangListResource;
}

export interface VorgangWithEingangAction {
  vorgangWithEingang: VorgangWithEingangResource;
}

export interface VorgangExportAction extends VorgangWithEingangAction {
  linkRel: VorgangWithEingangLinkRel;
}

export interface VorgangUriWithContextProps extends ResourceUriProps {
  additionalActions?: AdditionalActions;
}

//VorgangList
const noOperation: TypedActionCreator = createAction('[Vorgang-Routing] No Operation');

const loadVorgangList: TypedActionCreatorWithProps<ApiRootWithLinkRelProps> = createAction(
  '[Vorgang] Load VorgangList',
  props<ApiRootWithLinkRelProps>(),
);
const loadVorgangListSuccess: TypedActionCreatorWithProps<VorgangListAction> = createAction(
  '[Vorgang] Load VorgangList Success',
  props<VorgangListAction>(),
);
const loadVorgangListFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang] Load VorgangList Failure',
  props<ApiErrorAction>(),
);

const searchVorgaengeBy: TypedActionCreatorWithProps<SearchVorgaengeByProps> = createAction(
  '[Vorgang] Search VorgangList',
  props<SearchVorgaengeByProps>(),
);
const searchVorgaengeBySuccess: TypedActionCreatorWithProps<VorgangListAction> = createAction(
  '[Vorgang] Search VorgangList Success',
  props<VorgangListAction>(),
);
const searchVorgaengeByFailure: TypedActionCreatorWithProps<HttpErrorAction> = createAction(
  '[Vorgang] Search VorgangList Failure',
  props<HttpErrorAction>(),
);

const loadNextPage: TypedActionCreator = createAction('[Vorgang] Load next VorgangList page');
const loadNextPageSuccess: TypedActionCreatorWithProps<VorgangListAction> = createAction(
  '[Vorgang] Load next VorgangList page Success',
  props<VorgangListAction>(),
);

const loadPrevPage: TypedActionCreator = createAction('[Vorgang] Load prev VorgangList page');
const loadPrevPageSuccess: TypedActionCreatorWithProps<VorgangListAction> = createAction(
  '[Vorgang] Load prev VorgangList page Success',
  props<VorgangListAction>(),
);

//Search
const searchForPreview: TypedActionCreatorWithProps<StringBasedProps> = createAction(
  '[Vorgang] Search for preview',
  props<StringBasedProps>(),
);
const searchForPreviewSuccess: TypedActionCreatorWithProps<VorgangListAction> = createAction(
  '[Vorgang] Search for preview Success',
  props<VorgangListAction>(),
);
const searchForPreviewFailure: TypedActionCreatorWithProps<HttpErrorAction> = createAction(
  '[Vorgang] Search for preview Failure',
  props<HttpErrorAction>(),
);

const clearSearchPreviewList: TypedActionCreator = createAction('[Vorgang] Clear search preview list');
const clearSearchString: TypedActionCreator = createAction('[Vorgang] Clear search string');

const setSearchString: TypedActionCreatorWithProps<StringBasedProps> = createAction(
  '[Vorgang] Set search string',
  props<StringBasedProps>(),
);

//VorgangWithEingang
const loadVorgangWithEingang: TypedActionCreatorWithProps<VorgangUriWithContextProps> = createAction(
  '[Vorgang] Load VorgangWithEingang',
  props<VorgangUriWithContextProps>(),
);
const loadVorgangWithEingangSuccess: TypedActionCreatorWithProps<VorgangWithEingangAction> = createAction(
  '[Vorgang] Load VorgangWithEingang Success',
  props<VorgangWithEingangAction>(),
);
const loadVorgangWithEingangFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang] Load VorgangWithEingang Failure',
  props<ApiErrorAction>(),
);

const clearVorgangWithEingang: TypedActionCreator = createAction('[Vorgang] Clear VorgangWithEingang');
const setReloadVorgangWithEingang: TypedActionCreator = createAction('[Vorgang] Set reload at VorgangWithEingang');

const clearLastSelectedVorgang: TypedActionCreator = createAction('[Vorgang] Clear lastSelectedVorgang');

const loadPendingCommandList: TypedActionCreatorWithProps<VorgangWithEingangAction> = createAction(
  '[Vorgang] Load pending command list',
  props<VorgangWithEingangAction>(),
);
const loadPendingCommandListSuccess: TypedActionCreatorWithProps<LoadCommandListSuccessProps> = createAction(
  '[Vorgang/API] Load pending command list Success',
  props<LoadCommandListSuccessProps>(),
);
const loadPendingCommandListFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang/API] Load pending command list Failure',
  props<ApiErrorAction>(),
);

const loadAttachmentList: TypedActionCreatorWithProps<VorgangWithEingangAction> = createAction(
  '[Vorgang] Load AttachmentList',
  props<VorgangWithEingangAction>(),
);
const loadAttachmentListSuccess: TypedActionCreatorWithProps<LoadBinaryFileListSuccessProps> = createAction(
  '[Vorgang] Load AttachmentList Success',
  props<LoadBinaryFileListSuccessProps>(),
);
const loadAttachmentListFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang] Load AttachmentList Failure',
  props<ApiErrorAction>(),
);

const loadRepresentationList: TypedActionCreatorWithProps<VorgangWithEingangAction> = createAction(
  '[Vorgang] Load RepresentationList',
  props<VorgangWithEingangAction>(),
);
const loadRepresentationListSuccess: TypedActionCreatorWithProps<LoadBinaryFileListSuccessProps> = createAction(
  '[Vorgang] Load RepresentationList Success',
  props<LoadBinaryFileListSuccessProps>(),
);
const loadRepresentationListFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang] Load RepresentationList Failure',
  props<ApiErrorAction>(),
);

const setForwardingSingleCommand: TypedActionCreatorWithProps<CommandStateResourceProps> = createAction(
  '[Vorgang] Set forward command',
  props<CommandStateResourceProps>(),
);
const setForwardingSingleCommandLoading: TypedActionCreator = createAction('[Vorgang] Set forward command loading');
const setSendPostfachNachrichtSingleCommand: TypedActionCreatorWithProps<CommandStateResourceProps> = createAction(
  '[Vorgang] Set send postfach nachricht command',
  props<CommandStateResourceProps>(),
);
const setSendPostfachNachrichtSingleCommandLoading: TypedActionCreator = createAction(
  '[Vorgang] Set send postfach nachricht command loading',
);

const assignUser = createAction('[Vorgang] Assign User', props<{ userUri: ResourceUri }>());
const assignUserSuccess = createAction('[Vorgang] Assign User Success');

const unassignUser = createAction('[Vorgang] Unassign User');
const unassignUserSuccess = createAction('[Vorgang] Unassign User Success');

const exportVorgang: TypedActionCreatorWithProps<VorgangExportAction> = createAction(
  '[Vorgang] Export Vorgang',
  props<VorgangExportAction>(),
);
const exportVorgangSuccess: TypedActionCreator = createAction('[Vorgang] Export Vorgang Success');
const exportVorgangFailure: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Vorgang] Export Vorgang Failure',
  props<ApiErrorAction>(),
);

const setPendingCommandLoading: TypedActionCreatorWithProps<ResourceUriProps> = createAction(
  '[Vorgang] Set pending command loading',
  props<ResourceUriProps>(),
);

const updatePendingCommand: TypedActionCreatorWithProps<UpdateCommandProps> = createAction(
  '[Vorgang] Update pending command',
  props<UpdateCommandProps>(),
);

export const VorgangActions = {
  //VorgangList
  loadVorgangList,
  loadVorgangListSuccess,
  loadVorgangListFailure,
  searchVorgaengeBy,
  searchVorgaengeBySuccess,
  searchVorgaengeByFailure,
  loadNextPage,
  loadNextPageSuccess,
  loadPrevPage,
  loadPrevPageSuccess,
  //Search
  searchForPreview,
  searchForPreviewSuccess,
  searchForPreviewFailure,
  clearSearchPreviewList,
  clearSearchString,
  setSearchString,
  //VorgangWithEingang
  loadVorgangWithEingang,
  loadVorgangWithEingangSuccess,
  loadVorgangWithEingangFailure,
  clearVorgangWithEingang,
  setReloadVorgangWithEingang,
  clearLastSelectedVorgang,
  loadPendingCommandList,
  loadPendingCommandListSuccess,
  loadPendingCommandListFailure,
  loadAttachmentList,
  loadAttachmentListSuccess,
  loadAttachmentListFailure,
  loadRepresentationList,
  loadRepresentationListSuccess,
  loadRepresentationListFailure,
  setForwardingSingleCommand,
  setForwardingSingleCommandLoading,
  setSendPostfachNachrichtSingleCommand,
  setSendPostfachNachrichtSingleCommandLoading,
  assignUser,
  assignUserSuccess,
  unassignUser,
  unassignUserSuccess,
  exportVorgang,
  exportVorgangSuccess,
  exportVorgangFailure,
  setPendingCommandLoading,
  updatePendingCommand,
};
